// Port of SuiteModel/SceneShape.swift (review B29) and `DocumentStore.sceneShape(in:)`: the shape of a book scene by
// scene — how long each scene runs, how long its sentences are, how much of it is dialogue, and how varied its
// vocabulary is — with the scenes that stand out from their neighbours. Described, never graded. Built from the measures
// Statistics uses (sentence lengths, dialogue balance, prose words), so the figures agree.
import type { OpenedProject } from '../project.ts';
import { StandardField } from '../project.ts';
import type { ArrangementTreeNode } from '../model/arrangement.ts';
import { plainText, wordCount } from '../proseMarkdown.ts';
import { dialogueShare, measureDialogue, quoteStyleOf } from '../text/dialogueBalance.ts';
import { PROSE_WORD } from '../text/proseWords.ts';
import { measureSentenceLengths, sentenceMean } from '../text/readability.ts';
import { reference } from '../presence.ts';

export interface ShapeScene {
  id: string;
  title: string;
  /** The chapter (or part) it belongs to, for labels. */
  chapter: string | null;
  pov: string | null;
  words: number;
  /** Words in the book before this scene: where it starts on a reading-order axis. */
  start: number;
  sentences: number;
  /** Average words per sentence. */
  sentenceLength: number;
  /** Share of words inside quotation marks (0…1). */
  dialogue: number;
  /** Moving-average type–token ratio over 100-word windows (0…1; higher = more varied); null under 100 words. */
  vocabulary: number | null;
}

export interface ShapeAverages { words: number; sentenceLength: number; dialogue: number; vocabulary: number | null }

export type ShapeMeasure = 'length' | 'sentences' | 'dialogue' | 'vocabulary';

/** A scene that differs markedly from the scenes around it: something to look at, not a fault. */
export interface ShapeNotable {
  sceneID: string;
  measure: ShapeMeasure;
  message: string;
  /** How far it stands out, for ordering. */
  strength: number;
  id: string;
}

export interface SceneShape {
  scenes: ShapeScene[];
  averages: ShapeAverages;
  notable: ShapeNotable[];
}

export interface ShapeInput { id: string; title: string; chapter?: string | null; pov?: string | null; text: string }

/** Words a window of the vocabulary measure holds. */
export const VOCABULARY_WINDOW = 100;

/** Scenes with almost no text yet (under 50 words): placeholders, listed apart from the shape. */
export const unwritten = (shape: SceneShape) => shape.scenes.filter((s) => s.words < 50).map((s) => s.id);
export const totalWords = (shape: SceneShape) => { const last = shape.scenes[shape.scenes.length - 1]; return last ? last.start + last.words : 0; };

/** `SceneShape.measure`. `locale` writes the notables' numbers (the Mac uses the device's). */
export function measureShape(inputs: ShapeInput[], locale = 'en-US'): SceneShape {
  const style = quoteStyleOf(inputs.map((i) => i.text));
  let start = 0;
  const scenes: ShapeScene[] = [];
  for (const input of inputs) {
    const words = wordCount(input.text);
    const lengths = measureSentenceLengths([input.text]);
    const dialogue = measureDialogue([input.text], style);
    scenes.push({
      id: input.id, title: input.title, chapter: input.chapter ?? null, pov: input.pov ?? null, words, start,
      sentences: lengths.lengths.length, sentenceLength: sentenceMean(lengths), dialogue: dialogueShare(dialogue), vocabulary: vocabulary(plainText(input.text)),
    });
    start += words;
  }
  // Averages by words, so a long scene counts for more than a short one.
  const total = Math.max(1, scenes.reduce((s, x) => s + x.words, 0));
  const sentenceTotal = scenes.reduce((s, x) => s + x.sentences, 0);
  const rich = scenes.filter((s) => s.vocabulary !== null);
  const richWords = rich.reduce((s, x) => s + x.words, 0);
  const averages: ShapeAverages = {
    words: scenes.length ? total / scenes.length : 0,
    sentenceLength: sentenceTotal === 0 ? 0 : scenes.reduce((s, x) => s + x.sentenceLength * x.sentences, 0) / sentenceTotal,
    dialogue: scenes.reduce((s, x) => s + x.dialogue * x.words, 0) / total,
    vocabulary: rich.length ? rich.reduce((s, x) => s + x.vocabulary! * x.words, 0) / richWords : null,
  };
  return { scenes, averages, notable: shapeNotables(scenes, locale) };
}

/** `SceneShape.vocabulary`: the share of distinct words in each 100-word window, averaged, so short and long scenes
 *  compare fairly (a plain type–token ratio falls as a text grows). */
export function vocabulary(plain: string): number | null {
  const words = (plain.match(PROSE_WORD) ?? []).map((w) => w.toLowerCase().replaceAll('’', "'"));
  const w = VOCABULARY_WINDOW;
  if (words.length < w) return null;
  const counts = new Map<string, number>();
  for (const word of words.slice(0, w)) counts.set(word, (counts.get(word) ?? 0) + 1);
  let sum = counts.size;
  for (let i = w; i < words.length; i++) {
    const out = words[i - w];
    const n = counts.get(out)!;
    if (n === 1) counts.delete(out); else counts.set(out, n - 1);
    counts.set(words[i], (counts.get(words[i]) ?? 0) + 1);
    sum += counts.size;
  }
  return sum / (words.length - w + 1) / w;
}

function formatters(locale: string) {
  const make = (o: Intl.NumberFormatOptions) => {
    try { return new Intl.NumberFormat(locale, o); } catch { return new Intl.NumberFormat('en-US', o); }
  };
  // `.number.precision(.fractionLength(0))` and `.percent…`: Foundation rounds half to even.
  const n = make({ maximumFractionDigits: 0, roundingMode: 'halfEven' } as Intl.NumberFormatOptions);
  const pct = make({ style: 'percent', maximumFractionDigits: 0, roundingMode: 'halfEven' } as Intl.NumberFormatOptions);
  return { n: (x: number) => n.format(x), pct: (x: number) => pct.format(x) };
}

/** `SceneShape.notable`: scenes that differ markedly from the (up to) three either side, strongest first, at most eight. */
export function shapeNotables(scenes: readonly ShapeScene[], locale = 'en-US'): ShapeNotable[] {
  const found: ShapeNotable[] = [];
  const median = (values: number[]): number | null => {
    if (!values.length) return null;
    const s = [...values].sort((a, b) => a - b);
    return s.length % 2 === 1 ? s[(s.length - 1) / 2] : (s[s.length / 2 - 1] + s[s.length / 2]) / 2;
  };
  const neighbours = (i: number, value: (s: ShapeScene) => number | null): number | null => {
    const values: number[] = [];
    for (let j = Math.max(0, i - 3); j <= Math.min(scenes.length - 1, i + 3); j++) {
      if (j === i || scenes[j].words < 150) continue;
      const v = value(scenes[j]);
      if (v !== null) values.push(v);
    }
    return values.length >= 2 ? median(values) : null;
  };
  const { n, pct } = formatters(locale);
  const add = (s: ShapeScene, measure: ShapeMeasure, message: string, strength: number) =>
    found.push({ sceneID: s.id, measure, message, strength, id: s.id + measure });
  scenes.forEach((scene, i) => {
    const aroundWords = neighbours(i, (s) => s.words);
    if (aroundWords !== null && aroundWords > 0) {
      const ratio = scene.words / aroundWords;
      if (ratio >= 2.5 && scene.words >= 1000) {
        add(scene, 'length', `Much longer than the scenes around it (${n(scene.words)} words; they run about ${n(aroundWords)}).`, ratio);
      } else if (ratio <= 0.3 && aroundWords >= 800 && scene.words >= 100) {
        add(scene, 'length', `Much shorter than the scenes around it (${n(scene.words)} words; they run about ${n(aroundWords)}).`, 1 / Math.max(ratio, 0.01));
      }
    }
    if (!(scene.words >= 150 && scene.sentences >= 5)) return;
    const aroundLength = neighbours(i, (s) => (s.sentences >= 5 ? s.sentenceLength : null));
    if (aroundLength !== null && aroundLength > 0) {
      const ratio = scene.sentenceLength / aroundLength;
      if (ratio >= 1.5) {
        add(scene, 'sentences', `Longer sentences than the scenes around it (${n(scene.sentenceLength)} words on average; theirs about ${n(aroundLength)}): it may read more slowly.`, ratio * 1.6);
      } else if (ratio <= 0.6) {
        add(scene, 'sentences', `Shorter sentences than the scenes around it (${n(scene.sentenceLength)} words on average; theirs about ${n(aroundLength)}): it may read faster.`, 1.6 / ratio);
      }
    }
    const aroundDialogue = neighbours(i, (s) => s.dialogue);
    if (aroundDialogue !== null) {
      const difference = scene.dialogue - aroundDialogue;
      if (difference >= 0.45) {
        add(scene, 'dialogue', `Mostly dialogue (${pct(scene.dialogue)}) among scenes that are mostly narration (about ${pct(aroundDialogue)}).`, 2 + difference * 4);
      } else if (difference <= -0.45) {
        add(scene, 'dialogue', `Little dialogue (${pct(scene.dialogue)}) among scenes full of it (about ${pct(aroundDialogue)}).`, 2 - difference * 4);
      }
    }
    const aroundVocabulary = neighbours(i, (s) => s.vocabulary);
    if (scene.vocabulary !== null && aroundVocabulary !== null) {
      const difference = scene.vocabulary - aroundVocabulary;
      if (Math.abs(difference) >= 0.08) {
        add(scene, 'vocabulary', difference > 0
          ? 'A more varied vocabulary than the scenes around it: richer description, perhaps.'
          : 'A plainer vocabulary than the scenes around it: action or repetition, perhaps.', 2 + Math.abs(difference) * 20);
      }
    }
  });
  return found.map((f, i) => [f, i] as const).sort((a, b) => b[0].strength - a[0].strength || a[1] - b[1]).slice(0, 8).map(([f]) => f);
}

/** `String(format: "%.<digits>f")`: the exact value rounded, an exact tie to even (JS `toFixed` rounds ties away from
 *  zero). */
export function printfFixed(x: number, digits: number): string {
  const exact = x.toFixed(100);
  const dot = exact.indexOf('.');
  if (/^50*$/.test(exact.slice(dot + 1 + digits))) {
    const kept = exact.slice(0, digits ? dot + 1 + digits : dot);
    if (Number(kept.slice(-1)) % 2 === 0) return kept;
  }
  return x.toFixed(digits);
}

/** `SceneShape.csv`: the per-scene figures (RFC 4180, `\r\n`). */
export function shapeCSV(shape: SceneShape, povName: (id: string | null) => string = () => ''): string {
  const field = (s: string) => (/[,"\n\r\u000b\u000c\u0085\u2028\u2029]/.test(s) ? '"' + s.replaceAll('"', '""') + '"' : s);
  const rows = [['Scene', 'Chapter', 'POV', 'Words', 'Sentences', 'Words per sentence', 'Dialogue %', 'Vocabulary'].join(',')];
  for (const s of shape.scenes) {
    rows.push([field(s.title), field(s.chapter ?? ''), field(povName(s.pov)), String(s.words), String(s.sentences),
      printfFixed(s.sentenceLength, 1), printfFixed(s.dialogue * 100, 0), s.vocabulary !== null ? printfFixed(s.vocabulary, 3) : ''].join(','));
  }
  return rows.join('\r\n') + '\r\n';
}

/** `DocumentStore.sceneShape(in:)`: the manuscript's scenes in reading order (a folder only with text of its own),
 *  measured, each with its chapter (the nearest folder above it) and POV. Prose only: null for a screenplay. */
export function sceneShapeOf(project: OpenedProject, locale?: string): SceneShape | null {
  const record = project.record;
  const manuscript = record.arrangements.find((a) => a.role === 'manuscript');
  if (record.projectType === 'screenplay' || !manuscript) return null;
  const chapterOf = new Map<string, string>();
  const walk = (nodes: ArrangementTreeNode[], folder: string | null) => {
    for (const n of nodes) {
      let here = folder;
      const id = n.node.fragmentID;
      if (id !== undefined) {
        if (folder !== null) chapterOf.set(id, folder);
        if (n.node.isFolder || n.children.length) here = project.fragmentRecord(id)?.title ?? folder;
      }
      walk(n.children, here);
    }
  };
  walk(project.arrangementTree(manuscript.id), null);
  const inputs: ShapeInput[] = [];
  for (const scene of project.scenes(['prose'])) {
    const f = project.fragmentRecord(scene.id);
    if (!f) continue;
    if (scene.isFolder && wordCount(f.text) === 0) continue;
    inputs.push({ id: scene.id, title: f.title, chapter: chapterOf.get(scene.id) ?? null, pov: reference(f.metadata, StandardField.pov), text: f.text });
  }
  return measureShape(inputs, locale);
}
